import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { environment } from '../../../core/environments/environment';
import {
  AuthStatus,
  EndPoint,
  LoginRequestBody,
  LoginResponse,
  SendVerificationCodeResponse,
  UserRole,
  VerifyRecoveryPassCodeResponse,
} from '../../../core/models/auth.model';
import { Observable, tap, throwError } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import { UsersService } from '../../users/services/users.service';
import { jwtDecode } from 'jwt-decode';

import * as CryptoJS from 'crypto-js';
import { initialSesionUser } from '../../users/models/users.model';
import { SupportedLanguage } from '../../../core/services/i18n.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly _http = inject(HttpClient);
  private readonly _apiUrl = environment.apiUrl;
  private readonly _cookieService = inject(CookieService);
  private readonly _router = inject(Router);
  private readonly _userService = inject(UsersService);

  public tokenPassword = signal<string | undefined>(undefined);
  public accessToken = signal<string | null>(null);
  public authStatus = signal<AuthStatus>(AuthStatus.checking);

  public isPasswordRecovery = signal<boolean>(false);

  private readonly ENCRYPTION_PASSWORD = 'declarabid-a2be4654943758e5d205';

  private encrypt(text: string): string {
    const ciphertext = CryptoJS.AES.encrypt(
      text,
      this.ENCRYPTION_PASSWORD,
    ).toString();
    return ciphertext;
  }

  public login({
    email,
    password,
    language,
  }: LoginRequestBody): Observable<LoginResponse> {
    const encryptedEmail = this.encrypt(email);
    const encryptedPassword = this.encrypt(password);

    const payload = new HttpParams()
      .set('email', encryptedEmail)
      .set('password', encryptedPassword)
      .set('language', language);

    return this._http.post<LoginResponse>(
      `${this._apiUrl}${EndPoint.login}`,
      payload,
    );
  }

  public resendCode(email: string, password: string, language: SupportedLanguage): Observable<any> {
    const encryptedEmail = this.encrypt(email);
    const encryptedPass = this.encrypt(password);

    const payload = new HttpParams()
      .set('email', encryptedEmail)
      .set('password', encryptedPass)
      .set('language', language);

    return this._http.post<SendVerificationCodeResponse>(
      `${this._apiUrl}${EndPoint.login}`,
      payload,
    );
  }

  public sendRecoveryEmail(
    language: SupportedLanguage,
    email: string,
  ): Observable<any> {
    const encryptedEmail = this.encrypt(email);

    const payload = {
      email: encryptedEmail,
      language: language,
    };

    return this._http.post<SendVerificationCodeResponse>(
      `${this._apiUrl}${EndPoint.sendRecoveringPassEmail}`,
      payload,
    );
  }

  public resendRecoveryCode(
    language: SupportedLanguage,
    email: string,
  ): Observable<any> {
    const encryptedEmail = this.encrypt(email);

    const payload = {
      email: encryptedEmail,
      language: language,
    };

    return this._http.post<any>(
      `${this._apiUrl}${EndPoint.sendRecoveringPassEmail}`,
      payload,
    );
  }

  public verifyRecoveryCode(email: string, code: string): Observable<any> {
    const encryptedEmail = this.encrypt(email);

    const payload = {
      email: encryptedEmail,
      code: code,
    };
    return this._http
      .post<VerifyRecoveryPassCodeResponse>(
        `${this._apiUrl}${EndPoint.validateCodeRecoveringPasss}`,
        payload,
      )
      .pipe(
        tap((response) => {
          if (response.message === 'Confirmed code' && response.token) {
            this.tokenPassword.set(response.token);
          }
        }),
      );
  }

  public changePassword(newPassword: string): Observable<any> {
    const encryptedNewPassword = this.encrypt(newPassword);

    const token = this.tokenPassword();

    if (!token) {
      console.error('No hay un token de recuperación disponible');
      return throwError(() => new Error('Token de recuperación no disponible'));
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    });

    const payload = {
      newPassword: encryptedNewPassword,
    };

    return this._http.post<any>(
      `${this._apiUrl}${EndPoint.changePassword}`,
      payload,
      { headers },
    );
  }

  public redirectIfUserIsAuth = (authStatus: AuthStatus): boolean => {
    if (authStatus === AuthStatus.notAuthenticated) {
      return true;
    } else {
      this.redirectBasedOnUSerRole(
        this._userService.currentUser().role_value as UserRole,
      );
      return false;
    }
  };

  public redirectIfUserIsNotAuth = (authStatus: AuthStatus): boolean => {
    if (authStatus === AuthStatus.authenticated) {
      return true;
    } else {
      this._router.navigateByUrl('/auth/login');

      return false;
    }
  };

  public handleSuccessfulLogin(accessToken: string) {
    const role_value = this.restoreSessionFromToken(accessToken);
    this.redirectBasedOnUSerRole(role_value as UserRole);
  }

  private restoreSessionFromToken(accessToken: string): string {
    const {
      id,
      name,
      last_name,
      email,
      user_role_id,
      role_value,
      role_name,
      institutions,
    } = jwtDecode<{
      id: number;
      name: string;
      last_name: string;
      email: string;
      user_role_id: number;
      role_value: string;
      role_name: string;
      institutions: number[];
    }>(accessToken);

    this.storeToken(accessToken);

    const tokenInfoUser = {
      id,
      name,
      last_name,
      email,
      user_role_id,
      role_value,
      role_name,
      institutions,
    };

    this._userService.setCurrentUser(tokenInfoUser);
    this.authStatus.set(AuthStatus.authenticated);

    return role_value;
  }

  public reauthenticate() {
    const accessToken = this._cookieService.get('access_token');

    if (accessToken) {
      this.restoreSessionFromToken(accessToken);
    } else {
      this.logout();
    }

    return this.authStatus();
  }

  public checkRolePermission = async (userRoleCheck: UserRole) => {
    await this.waitForAuthStatusChange();

    const userRole = this._userService.currentUser().role_value;
    console.log('userRole', userRole);
    if (userRole === userRoleCheck) {
      return true;
    } else {
      this.redirectBasedOnUSerRole(userRole as UserRole);
      return false;
    }
  };

  public waitForAuthStatusChange = () => {
    return new Promise<void>((resolve) => {
      const checkAuthStatus = () => {
        const currentUser = this._userService.currentUser();
        if (currentUser) {
          resolve();
        } else {
          setTimeout(checkAuthStatus);
        }
      };

      checkAuthStatus();
    });
  };

  public redirectBasedOnUSerRole = (role: UserRole) => {
    switch (role) {
      case UserRole.admin:
        this._router.navigateByUrl('/admin/home');
        break;

      case UserRole.declarant:
        this._router.navigateByUrl('/declarant/dashboard');
        break;

      case UserRole.reviewer:
        this._router.navigateByUrl('/reviewer/dashboard');
        break;

      case UserRole.rh:
        this._router.navigateByUrl('/rh/dashboard');
        break;

      case UserRole.teacher:
        this._router.navigateByUrl('/teacher/dashboard');
        break;

      case UserRole.director:
        this._router.navigateByUrl('/director/dashboard');
        break;

      case UserRole.leader:
        this._router.navigateByUrl('/leader/home');
        break;
    }
  };

  public logout(): void {
    this._cookieService.delete('access_token', '/');
    this.accessToken.set(null);
    this.authStatus.set(AuthStatus.notAuthenticated);
    this._userService.setCurrentUser(initialSesionUser);

    this._router.navigateByUrl('/auth/login');
    this.deleteRecaptchaCookie();
  }

  public storeToken(token: string): void {
    this._cookieService.set('access_token', token, {
      path: '/',
      secure: true,
      sameSite: 'Lax',
    });

    this.accessToken.set(token);
  }

  public deleteRecaptchaCookie(): void {
    this._cookieService.delete('_GRECAPTCHA', '/');
    this._cookieService.delete('_GRECAPTCHA', '/recaptcha');
  }
}
